import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Clock, Calendar, Target,
  Sparkles, RefreshCw, Brain, Lightbulb, Rocket, GraduationCap,
  ShieldAlert, FileText, ChevronRight, Award, Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";
import EmployeeDossierPDF from "@/components/EmployeeDossierPDF";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};

const stepStatusConfig = {
  nao_iniciada: { label: "Não iniciada", icon: Clock, color: "bg-slate-100 text-slate-700 border-slate-200" },
  em_andamento: { label: "Em andamento", icon: Clock, color: "bg-sky-50 text-sky-700 border-sky-200" },
  concluida: { label: "Concluída", icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  atrasada: { label: "Atrasada", icon: AlertTriangle, color: "bg-rose-50 text-rose-700 border-rose-200" },
  reprovada: { label: "Reprovada", icon: ShieldAlert, color: "bg-rose-50 text-rose-700 border-rose-200" },
  dispensada: { label: "Dispensada", icon: CheckCircle2, color: "bg-slate-50 text-slate-500 border-slate-200" },
};

export default function OnboardingDetailPage() {
  const { employeeId } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showDossier, setShowDossier] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/onboarding/reports/${employeeId}`);
      setReport(data);
      if (data.assignment?.ai_summary) setAiAnalysis(data.assignment.ai_summary);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.info("Este colaborador ainda não possui onboarding atribuído.");
        nav("/performance/onboarding/atribuir");
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [employeeId]);

  const generateAi = async (force = false) => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/onboarding/ai-summary/${employeeId}${force ? "?force=true" : ""}`);
      setAiAnalysis(data);
      toast.success(data.from_cache ? "Resumo carregado (cache)." : "Resumo gerado pela IA.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao gerar resumo.");
    } finally { setAiLoading(false); }
  };

  if (loading || !report) {
    return <div className="space-y-3 pb-10"><Skeleton className="h-[180px]" /><Skeleton className="h-[400px]" /></div>;
  }

  const { assignment, employee, by_periodo, evaluations, recommended_trainings, recomendacao_final } = report;

  return (
    <div className="space-y-5 pb-12" data-testid="onboarding-detail-page">
      <Link to="/performance/onboarding"
            className="inline-flex items-center text-[12px] text-muted-foreground hover:text-[#234935] transition-colors"
            data-testid="onb-detail-back">
        <ArrowLeft className="h-3 w-3 mr-1" /> Voltar para o dashboard
      </Link>

      <PageHero
        kicker={<><Rocket className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Onboarding</>}
        title={assignment.employee_name}
        description={`${assignment.cargo} · ${assignment.sector_name} · Trilha: ${assignment.template_name}`}
        actions={
          <Button size="sm" onClick={() => setShowDossier(true)}
                  className="h-10 bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid="onb-dossier-btn">
            <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Gerar Dossiê PDF
          </Button>
        }
      />

      <EmployeeDossierPDF employeeId={employeeId}
                          open={showDossier}
                          onClose={() => setShowDossier(false)} />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="onb-detail-kpis">
        <KpiCard label="Progresso" value={`${assignment.progress_pct}%`}
                 sub={`${assignment.concluidas}/${assignment.total_steps} etapas`}
                 icon={Target} accent="emerald" testId="onb-d-progress" />
        <KpiCard label="Dias na trilha" value={`${assignment.days_since_start}/90`}
                 sub={`Período atual: ${assignment.current_period}d`}
                 icon={Calendar} accent="ochre" testId="onb-d-days" />
        <KpiCard label="Status" value={(assignment.status || "").replace("_", " ")}
                 sub={`Atrasadas: ${assignment.atrasadas}`}
                 icon={Clock} accent={
                   assignment.status === "concluido" ? "emerald" :
                   assignment.status === "atrasado" ? "rose" :
                   assignment.status === "em_risco" ? "ochre" : "sky"
                 } testId="onb-d-status" />
        <KpiCard label="Recomendação"
                 value={recomendacao_final ? recomendacao_final : "Pendente"}
                 sub="90 dias"
                 icon={Award} accent={
                   recomendacao_final === "efetivar" ? "emerald" :
                   recomendacao_final === "desligar" ? "rose" :
                   recomendacao_final === "prorrogar" ? "ochre" : "slate"
                 } testId="onb-d-recom" />
      </div>

      <Tabs defaultValue="timeline" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="timeline" data-testid="onb-tab-timeline">Timeline 30/60/90</TabsTrigger>
          <TabsTrigger value="evaluations" data-testid="onb-tab-evals">Avaliações</TabsTrigger>
          <TabsTrigger value="trainings" data-testid="onb-tab-trainings">Treinamentos</TabsTrigger>
          <TabsTrigger value="ai" data-testid="onb-tab-ai" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#234935] data-[state=active]:to-[#2c5c43] data-[state=active]:text-white">
            <Sparkles className="h-3 w-3 mr-1" /> IA
          </TabsTrigger>
        </TabsList>

        {/* TIMELINE */}
        <TabsContent value="timeline" className="mt-4">
          <div className="space-y-3">
            {by_periodo.map((p) => (
              <PeriodTimeline key={p.periodo} periodo={p.periodo} stats={p}
                              steps={assignment.steps.filter((s) => s.periodo === p.periodo)}
                              assignmentId={assignment.id}
                              currentPeriod={assignment.current_period}
                              onUpdate={load} />
            ))}
          </div>
        </TabsContent>

        {/* EVALUATIONS */}
        <TabsContent value="evaluations" className="mt-4 space-y-3">
          <Section kicker="Avaliações" title="30 / 60 / 90 dias"
                   action={<NewEvaluationDialog assignmentId={assignment.id} onSaved={load} />}>
            {evaluations.length === 0 ? (
              <div className="text-center py-10 text-[13px] text-muted-foreground">
                Sem avaliações registradas.
              </div>
            ) : (
              <div className="space-y-3">
                {evaluations.sort((a, b) => a.periodo - b.periodo).map((e) => (
                  <EvaluationCard key={e.periodo} ev={e} />
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* TRAININGS */}
        <TabsContent value="trainings" className="mt-4">
          <Section kicker="Sugestões" title="Treinamentos recomendados"
                   description="Baseado nos pontos críticos identificados nas avaliações 30/60/90.">
            {recommended_trainings.length === 0 ? (
              <div className="text-center py-8 text-[12.5px] text-muted-foreground">
                <GraduationCap className="h-8 w-8 mx-auto text-[#C5A059] mb-2" />
                Sem treinamentos críticos sugeridos no momento.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {recommended_trainings.map((t) => (
                  <div key={t.training_id}
                       className="rounded-lg border-2 border-[#C5A059]/40 bg-gradient-to-br from-[#FAF4E5] to-white p-3"
                       data-testid={`onb-rec-${t.training_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-medium text-[13px] truncate">{t.name}</div>
                      <Badge variant="outline" className="text-[9px] bg-[#234935] text-white border-[#234935]">
                        Score {t.score}
                      </Badge>
                    </div>
                    <ul className="text-[11px] text-slate-700 space-y-0.5 mt-1">
                      {t.reasons.map((r, idx) => (
                        <li key={`${t.training_id}-${idx}`} className="flex items-start gap-1">
                          <span className="text-[#C5A059] mt-0.5">›</span><span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Section>
          <div className="mt-3 text-center">
            <Link to={`/performance/individuos/${employeeId}`}
                  className="text-[12px] text-[#234935] hover:underline inline-flex items-center gap-1">
              Ver dossiê completo de performance <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai" className="mt-4">
          <Section
            kicker="Inteligência"
            title="Resumo executivo do onboarding"
            description="Diagnóstico gerado por Claude Sonnet 4.5 considerando etapas, avaliações e tempo na trilha."
            action={
              aiAnalysis ? (
                <Button size="sm" variant="outline" onClick={() => generateAi(true)} disabled={aiLoading}
                        data-testid="onb-ai-regen">
                  <RefreshCw className={`h-3 w-3 mr-1 ${aiLoading ? "animate-spin" : ""}`} /> Regenerar
                </Button>
              ) : (
                <Button size="sm" onClick={() => generateAi(false)} disabled={aiLoading}
                        className="bg-gradient-to-r from-[#234935] to-[#2c5c43] text-white"
                        data-testid="onb-ai-gen">
                  <Sparkles className={`h-3 w-3 mr-1 ${aiLoading ? "animate-pulse" : ""}`} />
                  {aiLoading ? "Analisando..." : "Gerar resumo"}
                </Button>
              )
            }
          >
            {!aiAnalysis ? (
              <div className="text-center py-12 px-4 rounded-lg border-2 border-dashed border-[#C5A059]/30 bg-gradient-to-br from-[#FAF4E5]/30 to-white"
                   data-testid="onb-ai-empty">
                <Brain className="h-12 w-12 mx-auto text-[#C5A059] mb-3" />
                <div className="font-display text-[16px] font-semibold mb-1">Análise sob demanda</div>
                <p className="text-[12.5px] text-muted-foreground max-w-md mx-auto">
                  A IA vai ler o histórico de etapas e avaliações de {assignment.employee_name.split(" ")[0]} e
                  emitir uma recomendação fundamentada (efetivar / prorrogar / desligar).
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="onb-ai-result">
                <div className="rounded-lg bg-gradient-to-br from-[#234935] to-[#1A231F] text-white p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-[#C5A059]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059] font-semibold">Resumo executivo</span>
                  </div>
                  <p className="text-[14px] leading-relaxed">{aiAnalysis.analysis.resumo_executivo}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">Pontos fortes</div>
                    <ul className="space-y-1.5 text-[12.5px]">
                      {(aiAnalysis.analysis.pontos_fortes || []).map((p, idx) => (
                        <li key={`pf-${idx}-${p.slice(0, 8)}`} className="flex items-start gap-1.5">
                          <span className="text-emerald-700 mt-0.5">›</span><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
                    <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-2">Pontos de atenção</div>
                    <ul className="space-y-1.5 text-[12.5px]">
                      {(aiAnalysis.analysis.pontos_atencao || []).map((p, idx) => (
                        <li key={`pa-${idx}-${p.slice(0, 8)}`} className="flex items-start gap-1.5">
                          <span className="text-amber-700 mt-0.5">›</span><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="rounded-lg border border-[#C5A059]/30 bg-gradient-to-br from-[#FAF4E5]/40 to-white p-4">
                  <div className="text-[11px] uppercase tracking-wider text-[#8A6B2B] font-semibold mb-2">Recomendações</div>
                  <ul className="space-y-1.5 text-[12.5px]">
                    {(aiAnalysis.analysis.recomendacoes || []).map((r, idx) => (
                      <li key={`rec-${idx}-${r.slice(0, 8)}`} className="flex items-start gap-1.5">
                        <span className="text-[#C5A059] mt-0.5 font-bold">{idx + 1}.</span><span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <RecommendationCard rec={aiAnalysis.analysis.recomendacao_final}
                                    justificativa={aiAnalysis.analysis.justificativa} />
              </div>
            )}
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PeriodTimeline({ periodo, stats, steps, assignmentId, currentPeriod, onUpdate }) {
  const isPast = currentPeriod > periodo;
  const isCurrent = currentPeriod === periodo;

  return (
    <Section
      kicker={isPast ? "Concluído" : isCurrent ? "Em andamento" : "Futuro"}
      title={`Primeiros ${periodo} dias`}
      testId={`onb-timeline-${periodo}`}
      action={
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700">{stats.concluidas} concluídas</Badge>
          {stats.atrasadas > 0 && <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700">{stats.atrasadas} atrasadas</Badge>}
          {stats.pendentes > 0 && <Badge variant="outline" className="text-[9px]">{stats.pendentes} pendentes</Badge>}
        </div>
      }
    >
      <div className="space-y-1.5">
        {steps.length === 0 ? (
          <div className="text-[11px] text-muted-foreground italic">Sem etapas neste período.</div>
        ) : steps.map((s) => (
          <StepRow key={s.id} step={s} assignmentId={assignmentId} onUpdate={onUpdate} />
        ))}
      </div>
    </Section>
  );
}

function StepRow({ step, assignmentId, onUpdate }) {
  const cfg = stepStatusConfig[step.status] || stepStatusConfig.nao_iniciada;
  const Icon = cfg.icon;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status: step.status, observacao: step.observacao || "",
    aprovacao_gestor: step.aprovado_gestor || false,
    aprovacao_rh: step.aprovado_rh || false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/onboarding/assignments/${assignmentId}/steps/${step.id}`, form);
      toast.success("Etapa atualizada.");
      setEditing(false);
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-white p-2.5" data-testid={`onb-step-${step.id}`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 shrink-0 ${
          step.status === "concluida" ? "text-emerald-600" :
          step.status === "atrasada" ? "text-rose-600" :
          step.status === "em_andamento" ? "text-sky-600" : "text-muted-foreground"
        }`} />
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-medium truncate">{step.titulo}</div>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span>Prazo {fmtDate(step.data_prevista)}</span>
            {step.obrigatoria && <Badge variant="outline" className="text-[9px] px-1 py-0">Obrigatória</Badge>}
            {step.aprovacao_gestor && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-50">Gestor</Badge>}
            {step.aprovacao_rh && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-purple-50">RH</Badge>}
          </div>
        </div>
        <Badge variant="outline" className={`text-[9px] capitalize ${cfg.color}`}>{cfg.label}</Badge>
        <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)}
                data-testid={`onb-step-edit-${step.id}`}>
          {editing ? "Fechar" : "Atualizar"}
        </Button>
      </div>

      {editing && (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid={`onb-step-status-${step.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(stepStatusConfig).map(([k, c]) => (
                    <SelectItem key={k} value={k}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-3">
              {step.aprovacao_gestor && (
                <label className="flex items-center gap-1 text-[11px]">
                  <input type="checkbox" checked={form.aprovacao_gestor}
                         onChange={(e) => setForm({ ...form, aprovacao_gestor: e.target.checked })} />
                  Aprovado pelo gestor
                </label>
              )}
              {step.aprovacao_rh && (
                <label className="flex items-center gap-1 text-[11px]">
                  <input type="checkbox" checked={form.aprovacao_rh}
                         onChange={(e) => setForm({ ...form, aprovacao_rh: e.target.checked })} />
                  Aprovado pelo RH
                </label>
              )}
            </div>
          </div>
          <Textarea rows={2} placeholder="Observação..." value={form.observacao}
                    onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#234935] text-white"
                    data-testid={`onb-step-save-${step.id}`}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function EvaluationCard({ ev }) {
  const scores = [
    ["Técnico", ev.score_tecnico], ["Comportamental", ev.score_comportamental],
    ["Produtividade", ev.score_produtividade], ["Disciplina", ev.score_disciplina],
    ["Pontualidade", ev.pontualidade], ["Equipe", ev.relacionamento_equipe],
    ["Cultura", ev.aderencia_cultura],
  ].filter(([, v]) => v != null);
  return (
    <div className="rounded-lg border border-border bg-white p-3" data-testid={`onb-ev-${ev.periodo}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-[14px]">Avaliação · {ev.periodo} dias</div>
        <span className="text-[10.5px] text-muted-foreground">{fmtDate(ev.registered_at)} por {ev.registered_by_name}</span>
      </div>
      {scores.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {scores.map(([label, val]) => (
            <div key={label} className="rounded-md bg-slate-50 p-2 text-center">
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
              <div className="text-[15px] font-semibold tabular-nums text-[#234935]">{val}<span className="text-[9px] text-muted-foreground">/5</span></div>
            </div>
          ))}
        </div>
      )}
      {ev.feedback_gestor && (
        <div className="text-[12px] mb-1"><b className="text-[#8A6B2B]">Gestor:</b> {ev.feedback_gestor}</div>
      )}
      {ev.feedback_rh && (
        <div className="text-[12px] mb-1"><b className="text-[#8A6B2B]">RH:</b> {ev.feedback_rh}</div>
      )}
      {ev.dificuldades && (
        <div className="text-[12px] mb-1"><b>Dificuldades:</b> {ev.dificuldades}</div>
      )}
      {ev.plano_acao && (
        <div className="text-[12px]"><b>Plano de ação:</b> {ev.plano_acao}</div>
      )}
      {ev.periodo === 90 && ev.recomendacao_final && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <Badge variant="outline" className={
            ev.recomendacao_final === "efetivar" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
            ev.recomendacao_final === "desligar" ? "bg-rose-50 text-rose-700 border-rose-200" :
            "bg-amber-50 text-amber-800 border-amber-200"
          }>
            Recomendação: {ev.recomendacao_final}
          </Badge>
        </div>
      )}
    </div>
  );
}

function RecommendationCard({ rec, justificativa }) {
  const cfg = {
    efetivar: { color: "emerald", label: "Recomendação: EFETIVAR", icon: CheckCircle2 },
    prorrogar: { color: "amber", label: "Recomendação: PRORROGAR PERÍODO", icon: AlertTriangle },
    desligar: { color: "rose", label: "Recomendação: DESLIGAR", icon: ShieldAlert },
  }[rec] || { color: "slate", label: "Sem recomendação", icon: ShieldAlert };
  const Icon = cfg.icon;
  return (
    <div className={`rounded-lg border-2 p-4 ${
      cfg.color === "emerald" ? "border-emerald-300 bg-emerald-50/50" :
      cfg.color === "amber" ? "border-amber-300 bg-amber-50/50" :
      cfg.color === "rose" ? "border-rose-300 bg-rose-50/50" :
      "border-slate-200 bg-slate-50"
    }`} data-testid="onb-ai-rec">
      <div className="flex items-start gap-2">
        <Icon className={`h-5 w-5 shrink-0 ${
          cfg.color === "rose" ? "text-rose-700" :
          cfg.color === "amber" ? "text-amber-700" :
          cfg.color === "emerald" ? "text-emerald-700" : "text-slate-500"
        }`} />
        <div>
          <div className="font-display font-bold text-[14px]">{cfg.label}</div>
          <p className="text-[12px] text-slate-700 mt-1">{justificativa}</p>
        </div>
      </div>
    </div>
  );
}

function NewEvaluationDialog({ assignmentId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    periodo: 30, score_tecnico: "", score_comportamental: "", score_produtividade: "",
    score_disciplina: "", pontualidade: "", relacionamento_equipe: "", aderencia_cultura: "",
    feedback_gestor: "", feedback_rh: "", dificuldades: "", plano_acao: "", recomendacao_final: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { periodo: Number(form.periodo) };
      [
        "score_tecnico", "score_comportamental", "score_produtividade", "score_disciplina",
        "pontualidade", "relacionamento_equipe", "aderencia_cultura",
      ].forEach((k) => { if (form[k] !== "") payload[k] = Number(form[k]); });
      ["feedback_gestor", "feedback_rh", "dificuldades", "plano_acao"].forEach((k) => {
        if (form[k]) payload[k] = form[k];
      });
      if (form.periodo === 90 && form.recomendacao_final) payload.recomendacao_final = form.recomendacao_final;
      await api.post(`/onboarding/assignments/${assignmentId}/period-evaluation`, payload);
      toast.success("Avaliação registrada.");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-[#C5A059] text-[#1A231F]" data-testid="onb-ev-new-btn">
          <FileText className="h-3 w-3 mr-1" /> Nova avaliação
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" aria-describedby="ev-desc">
        <DialogHeader><DialogTitle>Avaliação periódica</DialogTitle></DialogHeader>
        <p id="ev-desc" className="sr-only">Formulário de avaliação periódica do onboarding</p>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Período</Label>
            <Select value={String(form.periodo)} onValueChange={(v) => setForm({ ...form, periodo: Number(v) })}>
              <SelectTrigger data-testid="onb-ev-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="60">60 dias</SelectItem>
                <SelectItem value="90">90 dias (final)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              ["score_tecnico", "Técnico"],
              ["score_comportamental", "Compor."],
              ["score_produtividade", "Produt."],
              ["score_disciplina", "Disciplina"],
              ["pontualidade", "Pontual."],
              ["relacionamento_equipe", "Equipe"],
              ["aderencia_cultura", "Cultura"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-[10px] uppercase tracking-wider">{label}</Label>
                <Input type="number" step="0.5" min="0" max="5" value={form[k]}
                       onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-8 text-[12px]" />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Feedback do gestor</Label>
            <Textarea rows={2} value={form.feedback_gestor}
                      onChange={(e) => setForm({ ...form, feedback_gestor: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Dificuldades observadas</Label>
            <Textarea rows={2} value={form.dificuldades}
                      onChange={(e) => setForm({ ...form, dificuldades: e.target.value })} />
          </div>
          {form.periodo === 90 && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Recomendação final</Label>
              <Select value={form.recomendacao_final} onValueChange={(v) => setForm({ ...form, recomendacao_final: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efetivar">Efetivar</SelectItem>
                  <SelectItem value="prorrogar">Prorrogar</SelectItem>
                  <SelectItem value="desligar">Desligar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-[#234935] text-white"
                  data-testid="onb-ev-save">
            {saving ? "Salvando..." : "Registrar avaliação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
